import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { TournamentProvider } from "./context/TournamentContext";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import HomePage from "./pages/HomePage";
import SetupPage from "./pages/SetupPage";
import BracketPage from "./pages/BracketPage";
import LeaguePage from "./pages/LeaguePage";
import ChampionPage from "./pages/ChampionPage";
import SharePage from "./pages/SharePage";
import PlayerStatsPage from "./pages/PlayerStatsPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ProfilePage from "./pages/ProfilePage";
import AdminDashboard from "./pages/AdminDashboard";
import { Trophy } from "lucide-react";
import "./App.css";

function AuthLoading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505]">
      <div className="w-14 h-14 bg-neon-cyan/20 rounded flex items-center justify-center mb-4 animate-pulse">
        <Trophy className="w-8 h-8 text-neon-cyan" />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Process the Google OAuth redirect FIRST (synchronously, before other routes)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <SetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/bracket/:id" element={<BracketPage />} />
      <Route path="/league/:id" element={<LeaguePage />} />
      <Route path="/champion/:id" element={<ChampionPage />} />
      <Route path="/stats/:id" element={<PlayerStatsPage />} />
      <Route path="/t/:id" element={<SharePage />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    fetch(`${url}/api/track/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: window.location.pathname || "/" }),
    }).catch(() => {});
  }, []);
  return (
    <LanguageProvider>
      <AuthProvider>
        <TournamentProvider>
          <div className="App min-h-screen bg-[#050505]">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </div>
        </TournamentProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
